// src/services/payment/paymentService.v2.ts
/**
 * ✅ FRONTEND PAYMENT SERVICE V2 - COMPLETE & MERGED
 * Tích hợp với apiClient từ project
 * CHỈ GỌI BACKEND API - KHÔNG CÓ SECRET KEYS
 */

import apiClient from '@/api/apiClient';
import {
  Payment,
  PaymentStatus,
  PaymentMethod,
  PaymentGateway,
  CreatePaymentData,
  PaymentGatewayResponse,
  PaymentFilters,
  PaymentStats,
  InstallmentPaymentData,
  RefundReason
} from '@/types/payments';
import { getCurrentUserId } from '@/utils/authHelper';

class PaymentServiceV2 {

  // ========================================
  // POPULATE HELPERS (NEW - following marketplaceService pattern)
  // ========================================

  /**
   * Build populate params for payment queries
   * @param includeNested - Include nested relations (license_type, etc.)
   */
  private buildPopulateParams(includeNested: boolean = true): Record<string, string> {
    if (includeNested) {
      return {
        'populate[payer][fields][0]': 'id',
        'populate[payer][fields][1]': 'username',
        'populate[payer][fields][2]': 'email',
        'populate[receiver][fields][0]': 'id',
        'populate[receiver][fields][1]': 'username',
        'populate[license]': 'true', // Full populate license (includes license_type)
        'populate[license_purchase]': 'true'
      };
    } else {
      return {
        'populate[payer][fields][0]': 'id',
        'populate[payer][fields][1]': 'username',
        'populate[receiver][fields][0]': 'id',
        'populate[receiver][fields][1]': 'username'
      };
    }
  }

  /**
   * Build filter params for payment queries
   */
  private buildFilterParams(filters: PaymentFilters = {}): Record<string, string> {
    const params: Record<string, string> = {};

    if (filters.status?.length) {
      filters.status.forEach((status, i) => {
        params[`filters[paymentStatus][$in][${i}]`] = status;
      });
    }

    if (filters.method?.length) {
      filters.method.forEach((method, i) => {
        params[`filters[paymentMethod][$in][${i}]`] = method;
      });
    }

    if (filters.dateFrom) {
      params['filters[createdAt][$gte]'] = filters.dateFrom;
    }

    if (filters.dateTo) {
      params['filters[createdAt][$lte]'] = filters.dateTo;
    }

    if (filters.amountMin) {
      params['filters[amount][$gte]'] = filters.amountMin.toString();
    }

    if (filters.amountMax) {
      params['filters[amount][$lte]'] = filters.amountMax.toString();
    }

    return params;
  }

  /**
   * Build complete query params (NEW - more organized)
   */
  private buildQueryParams(
    filters: Record<string, string>,
    page: number = 1,
    pageSize: number = 20,
    includeNested: boolean = true,
    sort: string = 'createdAt:desc'
  ): Record<string, string> {
    return {
      ...filters,
      'pagination[page]': page.toString(),
      'pagination[pageSize]': pageSize.toString(),
      'sort': sort,
      ...this.buildPopulateParams(includeNested)
    };
  }

  // ========================================
  // CREATE PAYMENT WITH GATEWAY
  // ========================================

  /**
   * Tạo payment - Backend sẽ xử lý gateway integration
   */
  async createPayment(data: CreatePaymentData): Promise<PaymentGatewayResponse> {
    try {
      console.log('🆕 Creating payment via backend API');

      this.validatePaymentData(data);

      const userId = getCurrentUserId();
      if (!userId) {
        throw new Error('Vui lòng đăng nhập để tiếp tục');
      }

      const response = await apiClient.post<PaymentGatewayResponse>(
        '/api/payments/create',
        {
          amount: data.amount,
          currency: data.currency || 'VND',
          paymentMethod: data.paymentMethod,
          description: data.description,
          customerEmail: data.customerEmail,
          customerPhone: data.customerPhone,
          customerName: data.customerName,
          licenseRequestId: data.licenseRequestId,
          metadata: data.metadata,
          isInstallment: data.isInstallment,
          installmentPlan: data.installmentPlan
        }
      );

      console.log('✅ Payment created:', response.data.paymentId);

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to create payment:', error);
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Không thể tạo thanh toán. Vui lòng thử lại.'
      );
    }
  }

  // ========================================
  // PAYMENT REDIRECT
  // ========================================

  /**
   * Redirect user đến payment gateway
   */
  redirectToPayment(paymentUrl: string): void {
    if (!paymentUrl) {
      throw new Error('Payment URL is required');
    }

    console.log('🔄 Redirecting to payment gateway...');

    sessionStorage.setItem('payment_return_url', window.location.href);

    window.location.href = paymentUrl;
  }

  /**
   * Handle payment return from gateway
   */
  async handlePaymentReturn(params: URLSearchParams): Promise<Payment> {
    try {
      console.log('🔄 Processing payment return');

      const paymentId =
        params.get('paymentId') ||
        params.get('vnp_TxnRef') ||
        params.get('orderId');

      if (!paymentId) {
        throw new Error('Payment ID not found in return parameters');
      }

      const payment = await this.getPaymentByPaymentId(paymentId);

      if (!payment) {
        throw new Error('Payment not found');
      }

      console.log('✅ Payment return processed:', {
        paymentId: payment.paymentId,
        status: payment.paymentStatus
      });

      return payment;
    } catch (error: any) {
      console.error('❌ Failed to process payment return:', error);
      throw new Error(
        error.message ||
        'Không thể xác nhận thanh toán'
      );
    }
  }

  // ========================================
  // QUERY PAYMENTS
  // ========================================

  /**
   * Lấy payment theo documentId
   */
  /**
   * Lấy payment theo documentId
   */
  async getPaymentById(documentId: string): Promise<Payment> {
    try {
      // Use raw params instead of object
      const params = {
        ...this.buildPopulateParams(true)
      };

      const response = await apiClient.get<any>(
        `/api/payments/${documentId}`,
        { params }
      );

      if (!response.data?.data) {
        throw new Error('Payment not found');
      }

      // Normalize Strapi response
      return {
        id: response.data.data.id,
        documentId: response.data.data.documentId,
        ...response.data.data
      };
    } catch (error: any) {
      console.error('Failed to fetch payment:', error);
      throw new Error('Không thể tải thông tin thanh toán');
    }
  }

  /**
   * Lấy payment theo paymentId (VTR-PAY-xxx)
   */
  /**
   * Lấy payment theo paymentId (VTR-PAY-xxx)
   */
  async getPaymentByPaymentId(paymentId: string): Promise<Payment | null> {
    try {
      console.log('🔍 Searching payment by paymentId:', paymentId);

      // ✅ Use buildQueryParams helper
      const params = this.buildQueryParams(
        { 'filters[paymentId][$eq]': paymentId },
        1,    // page
        1,    // pageSize  
        true  // includeNested
      );

      const response = await apiClient.get<any>(
        '/api/payments',
        { params }
      );

      if (!response.data?.data?.length) {
        console.warn('⚠️ Payment not found:', paymentId);
        return null;
      }

      const payment = response.data.data[0];

      console.log('✅ Payment found:', payment.paymentId);

      return {
        id: payment.id,
        documentId: payment.documentId,
        ...payment
      };
    } catch (error) {
      console.error('Failed to fetch payment by paymentId:', error);
      return null;
    }
  }

  /**
   * Query payment status từ backend
   */
  async queryPaymentStatus(paymentId: string): Promise<{
    status: PaymentStatus;
    amount?: number;
    paidAt?: string;
  }> {
    try {
      const response = await apiClient.get<any>(
        `/api/payments/status/${paymentId}`
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to query payment status:', error);
      throw new Error('Không thể kiểm tra trạng thái thanh toán');
    }
  }

  /**
   * Lấy danh sách payments của user
   */
  async getUserPayments(
    userId: string,
    filters?: PaymentFilters,
    page: number = 1,
    pageSize: number = 20
  ): Promise<{ data: Payment[]; meta: any }> {
    try {
      // Build base filters
      const baseFilters: Record<string, string> = {
        'filters[$or][0][payer][documentId][$eq]': userId,
        'filters[$or][1][receiver][documentId][$eq]': userId
      };

      // Merge with additional filters
      const additionalFilters = filters ? this.buildFilterParams(filters) : {};
      const allFilters = { ...baseFilters, ...additionalFilters };

      // Use buildQueryParams helper
      const params = this.buildQueryParams(
        allFilters,
        page,
        pageSize,
        true
      );

      const response = await apiClient.get<any>(
        '/api/payments',
        { params }
      );

      // Normalize response
      const payments = (response.data?.data || []).map((item: any) => ({
        id: item.id,
        documentId: item.documentId,
        ...item
      }));

      return {
        data: payments,
        meta: response.data?.meta || {}
      };
    } catch (error) {
      console.error('Failed to fetch user payments:', error);
      throw new Error('Không thể tải danh sách thanh toán');
    }
  }

  /**
   * Lấy pending payments của user
   */
  async getPendingPayments(userId: string): Promise<Payment[]> {
    try {
      const params: Record<string, string> = {
        'filters[payer][documentId][$eq]': userId,
        'filters[paymentStatus][$eq]': PaymentStatus.PENDING,
        'pagination[pageSize]': '100',
        'sort': 'createdAt:desc',
        ...this.buildPopulateParams(true)
      };

      const response = await apiClient.get<any>(
        '/api/payments',
        { params }
      );

      return (response.data?.data || []).map((item: any) => ({
        id: item.id,
        documentId: item.documentId,
        ...item
      }));
    } catch (error) {
      console.error('Failed to fetch pending payments:', error);
      return [];
    }
  }

  // ========================================
  // LICENSE PAYMENT OPERATIONS
  // ========================================

  /**
   * Lấy payments của một license
   */
  async getLicensePayments(licenseId: string): Promise<Payment[]> {
    try {
      const params: Record<string, string> = {
        'filters[license][documentId][$eq]': licenseId,
        'pagination[pageSize]': '100',
        'sort': 'createdAt:desc',
        ...this.buildPopulateParams(false) // Don't need full nested for this
      };

      const response = await apiClient.get<any>(
        '/api/payments',
        { params }
      );

      return (response.data?.data || []).map((item: any) => ({
        id: item.id,
        documentId: item.documentId,
        ...item
      }));
    } catch (error) {
      console.error('Failed to fetch license payments:', error);
      throw new Error('Không thể tải danh sách thanh toán của license');
    }
  }

  // ========================================
  // PAYMENT COMPLETION & CANCELLATION
  // ========================================

  /**
   * Complete payment (sau khi gateway confirm)
   */
  async completePayment(
    documentId: string,
    gatewayTransactionId: string
  ): Promise<Payment> {
    try {
      const result = await apiClient.updateRecord<Payment>(
        'payment',
        documentId,
        {
          paymentStatus: PaymentStatus.COMPLETED,
          gatewayTransactionId,
          confirmedAt: new Date().toISOString(),
          transactionDate: new Date().toISOString()
        }
      );

      if (!result.success || !result.data) {
        throw new Error('Failed to complete payment');
      }

      console.log('✅ Payment completed:', result.data.paymentId);
      return result.data;
    } catch (error: any) {
      console.error('Complete payment error:', error);
      throw new Error('Không thể hoàn thành thanh toán');
    }
  }

  /**
   * Cancel payment
   */
  async cancelPayment(
    documentId: string,
    reason: string
  ): Promise<Payment> {
    try {
      const result = await apiClient.updateRecord<Payment>(
        'payment',
        documentId,
        {
          paymentStatus: PaymentStatus.CANCELLED,
          notes: reason,
          metadata: {
            cancellationReason: reason,
            cancelledAt: new Date().toISOString()
          }
        }
      );

      if (!result.success || !result.data) {
        throw new Error('Failed to cancel payment');
      }

      console.log('✅ Payment cancelled:', result.data.paymentId);
      return result.data;
    } catch (error: any) {
      console.error('Cancel payment error:', error);
      throw new Error('Không thể hủy thanh toán');
    }
  }

  // ========================================
  // PAYMENT VERIFICATION
  // ========================================

  /**
   * Verify payment với gateway
   */
  async verifyPayment(
    paymentId: string,
    gatewayTransactionId: string
  ): Promise<{
    verified: boolean;
    status: PaymentStatus;
    message?: string;
  }> {
    try {
      const response = await apiClient.post<{
        verified: boolean;
        status: PaymentStatus;
        message?: string;
      }>(
        `/api/payments/${paymentId}/verify`,
        { gatewayTransactionId }
      );

      return response.data;
    } catch (error: any) {
      console.error('Verify payment error:', error);
      throw new Error('Không thể xác minh thanh toán');
    }
  }

  // ========================================
  // PAYMENT STATS
  // ========================================

  /**
   * Lấy payment statistics
   */
  async getPaymentStats(userId: string, period?: string): Promise<PaymentStats> {
    try {
      const response = await apiClient.get<PaymentStats>(
        '/api/payments/stats',
        {
          params: { userId, period }
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to get payment stats:', error);

      return this.calculateStatsFromPayments(userId, period);
    }
  }

  /**
   * Calculate stats from payments (fallback)
   */
  private async calculateStatsFromPayments(
    userId: string,
    period?: string
  ): Promise<PaymentStats> {
    try {
      const filters: PaymentFilters = {};
      if (period) {
        filters.dateFrom = this.getPeriodStartDate(period);
      }

      const { data: payments } = await this.getUserPayments(userId, filters, 1, 1000);

      const completedPayments = payments.filter(p => p.paymentStatus === PaymentStatus.COMPLETED);
      const pendingPayments = payments.filter(p => p.paymentStatus === PaymentStatus.PENDING);

      const totalRevenue = completedPayments
        .filter(p => p.receiver?.documentId === userId)
        .reduce((sum, p) => sum + (p.amount || 0), 0);

      const thisMonth = this.getThisMonthPayments(completedPayments);
      const lastMonth = this.getLastMonthPayments(completedPayments);

      const thisMonthRevenue = thisMonth.reduce((sum, p) => sum + (p.amount || 0), 0);
      const lastMonthRevenue = lastMonth.reduce((sum, p) => sum + (p.amount || 0), 0);

      const platformCommission = completedPayments.reduce(
        (sum, p) => sum + (p.commission_amount || 0),
        0
      );
      const ownerRevenue = completedPayments.reduce(
        (sum, p) => sum + (p.seller_amount || 0),
        0
      );

      const averageTransactionValue = completedPayments.length > 0
        ? totalRevenue / completedPayments.length
        : 0;

      const successRate = payments.length > 0
        ? (completedPayments.length / payments.length) * 100
        : 0;

      const methodStats = this.calculateMethodStats(completedPayments);

      return {
        totalRevenue,
        thisMonthRevenue,
        lastMonthRevenue,
        pendingPayments: pendingPayments.length,
        completedPayments: completedPayments.length,
        platformCommission,
        ownerRevenue,
        averageTransactionValue: Math.round(averageTransactionValue),
        totalTransactions: payments.length,
        successRate: Math.round(successRate * 100) / 100,
        refundRate: 0,
        topPaymentMethods: methodStats
      };
    } catch (error) {
      console.error('Failed to calculate stats:', error);

      return {
        totalRevenue: 0,
        thisMonthRevenue: 0,
        lastMonthRevenue: 0,
        pendingPayments: 0,
        completedPayments: 0,
        platformCommission: 0,
        ownerRevenue: 0,
        averageTransactionValue: 0,
        totalTransactions: 0,
        successRate: 0,
        refundRate: 0,
        topPaymentMethods: []
      };
    }
  }

  // ========================================
  // REVENUE ANALYTICS
  // ========================================

  /**
   * Get revenue breakdown cho owner
   */
  async getRevenueBreakdown(
    ownerId: string,
    period: string = '30d'
  ): Promise<{
    totalRevenue: number;
    platformFee: number;
    ownerRevenue: number;
    transactions: number;
    breakdown: Array<{
      date: string;
      revenue: number;
      transactions: number;
    }>;
  }> {
    try {
      const response = await apiClient.get<any>(
        '/api/payments/revenue-breakdown',
        {
          params: { ownerId, period }
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Get revenue breakdown error:', error);

      return this.calculateRevenueBreakdownFromPayments(ownerId, period);
    }
  }

  /**
   * Calculate revenue breakdown from payments (fallback)
   */
  private async calculateRevenueBreakdownFromPayments(
    ownerId: string,
    period: string
  ): Promise<any> {
    try {
      const filters: PaymentFilters = {
        dateFrom: this.getPeriodStartDate(period)
      };

      const { data: payments } = await this.getUserPayments(ownerId, filters, 1, 1000);

      const completedPayments = payments.filter(
        p => p.paymentStatus === PaymentStatus.COMPLETED && p.receiver?.documentId === ownerId
      );

      const totalRevenue = completedPayments.reduce(
        (sum, p) => sum + (p.amount || 0),
        0
      );

      const platformFee = completedPayments.reduce(
        (sum, p) => sum + (p.commission_amount || 0),
        0
      );

      const ownerRevenue = completedPayments.reduce(
        (sum, p) => sum + (p.seller_amount || 0),
        0
      );

      const breakdown = this.groupPaymentsByDate(completedPayments);

      return {
        totalRevenue,
        platformFee,
        ownerRevenue,
        transactions: completedPayments.length,
        breakdown
      };
    } catch (error) {
      console.error('Calculate revenue breakdown error:', error);

      return {
        totalRevenue: 0,
        platformFee: 0,
        ownerRevenue: 0,
        transactions: 0,
        breakdown: []
      };
    }
  }

  /**
   * Group payments by date
   */
  private groupPaymentsByDate(payments: Payment[]): Array<{
    date: string;
    revenue: number;
    transactions: number;
  }> {
    const grouped: Record<string, { revenue: number; transactions: number }> = {};

    payments.forEach(payment => {
      const date = new Date(payment.createdAt).toISOString().split('T')[0];

      if (!grouped[date]) {
        grouped[date] = { revenue: 0, transactions: 0 };
      }

      grouped[date].revenue += payment.amount || 0;
      grouped[date].transactions += 1;
    });

    return Object.entries(grouped)
      .map(([date, stats]) => ({ date, ...stats }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  // ========================================
  // REFUND OPERATIONS
  // ========================================

  /**
   * Request refund (user yêu cầu)
   */
  async requestRefund(
    paymentId: string,
    amount: number,
    reason: string,
    reasonDetails?: string
  ): Promise<Payment> {
    try {
      console.log('💸 Requesting refund via backend');

      const response = await apiClient.post<Payment>(
        `/api/payments/${paymentId}/refund`,
        {
          amount,
          reason,
          reasonDetails
        }
      );

      console.log('✅ Refund requested successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to request refund:', error);
      throw new Error(
        error.response?.data?.message ||
        'Không thể yêu cầu hoàn tiền'
      );
    }
  }

  /**
   * Process refund (admin thực thi)
   */
  async processRefund(
    documentId: string,
    amount: number,
    reason: RefundReason,
    adminId: string
  ): Promise<Payment> {
    try {
      console.log('💸 Processing refund via backend (admin)');

      const response = await apiClient.post<Payment>(
        `/api/payments/${documentId}/process-refund`,
        {
          amount,
          reason,
          adminId
        }
      );

      console.log('✅ Refund processed successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to process refund:', error);
      throw new Error(
        error.response?.data?.message ||
        'Không thể xử lý hoàn tiền'
      );
    }
  }

  // ========================================
  // INSTALLMENT PAYMENTS
  // ========================================

  /**
   * Tạo installment payment
   */
  async createInstallmentPayment(data: InstallmentPaymentData): Promise<{
    parentPayment: Payment;
    installments: Payment[];
  }> {
    try {
      console.log('📅 Creating installment payment');

      const response = await apiClient.post<{
        parentPayment: Payment;
        installments: Payment[];
      }>(
        '/api/payments/installment',
        data
      );

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to create installment payment:', error);
      throw new Error(
        error.response?.data?.message ||
        'Không thể tạo thanh toán trả góp'
      );
    }
  }

  // ========================================
  // AVAILABLE PAYMENT METHODS
  // ========================================

  /**
   * Lấy danh sách payment methods available
   */
  async getAvailablePaymentMethods(): Promise<Array<{
    method: PaymentMethod;
    name: string;
    icon?: string;
    enabled: boolean;
    description?: string;
  }>> {
    try {
      const response = await apiClient.get<Array<{
        method: PaymentMethod;
        name: string;
        icon?: string;
        enabled: boolean;
        description?: string;
      }>>(
        '/api/payments/methods'
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get payment methods:', error);

      return [
        {
          method: PaymentMethod.MOMO,
          name: 'MoMo',
          enabled: true,
          description: 'Thanh toán qua ví MoMo',
          icon: '💰'
        },
        {
          method: PaymentMethod.VNPAY,
          name: 'VNPay',
          enabled: true,
          description: 'Thanh toán qua VNPay (ATM, Visa, MasterCard)',
          icon: '🏦'
        },
        {
          method: PaymentMethod.BANK_TRANSFER,
          name: 'Chuyển khoản',
          enabled: true,
          description: 'Chuyển khoản ngân hàng',
          icon: '🏛️'
        }
      ];
    }
  }

  /**
  * Confirm payment (admin only)
  */
  async confirmPayment(
    documentId: string,
    data: {
      confirmedBy: string;
      confirmedAt: string;
      notes?: string;
    }
  ): Promise<Payment> {
    try {
      const result = await apiClient.post<Payment>(
        `/api/payments/${documentId}/confirm`,
        data
      );
      return result.data;
    } catch (error: any) {
      console.error('Confirm payment error:', error);
      throw new Error('Không thể xác nhận thanh toán');
    }
  }

  /**
   * Reject payment (admin only)
   */
  async rejectPayment(
    documentId: string,
    data: {
      rejectedBy: string;
      rejectedAt: string;
      reason: string;
    }
  ): Promise<Payment> {
    try {
      const result = await apiClient.post<Payment>(
        `/api/payments/${documentId}/reject`,
        data
      );
      return result.data;
    } catch (error: any) {
      console.error('Reject payment error:', error);
      throw new Error('Không thể từ chối thanh toán');
    }
  }

  // ========================================
  // REVENUE CALCULATION
  // ========================================

  /**
   * Calculate revenue split (platform fee vs seller amount)
   */
  calculateRevenueSplit(
    totalAmount: number,
    commissionRate: number = 15
  ): {
    totalAmount: number;
    platformFee: number;
    sellerAmount: number;
    commissionRate: number;
  } {
    const platformFee = Math.round(totalAmount * (commissionRate / 100));
    const sellerAmount = totalAmount - platformFee;

    return {
      totalAmount,
      platformFee,
      sellerAmount,
      commissionRate
    };
  }

  /**
   * Calculate installment breakdown
   */
  calculateInstallmentBreakdown(
    totalAmount: number,
    numberOfInstallments: number,
    interestRate: number = 0
  ): Array<{
    installmentNumber: number;
    amount: number;
    interest: number;
    total: number;
    dueDate: Date;
  }> {
    const baseAmount = Math.floor(totalAmount / numberOfInstallments);
    const remainder = totalAmount % numberOfInstallments;

    const installments = [];
    const now = new Date();

    for (let i = 1; i <= numberOfInstallments; i++) {
      const amount = baseAmount + (i === numberOfInstallments ? remainder : 0);
      const interest = Math.round(amount * (interestRate / 100));
      const total = amount + interest;

      const dueDate = new Date(now);
      dueDate.setMonth(dueDate.getMonth() + i);

      installments.push({
        installmentNumber: i,
        amount,
        interest,
        total,
        dueDate
      });
    }

    return installments;
  }

  // ========================================
  // PAYMENT HISTORY & REPORTS
  // ========================================

  /**
   * Get payment history with filters
   */
  async getPaymentHistory(
    userId: string,
    options: {
      page?: number;
      pageSize?: number;
      startDate?: string;
      endDate?: string;
      status?: PaymentStatus[];
      method?: PaymentMethod[];
      minAmount?: number;
      maxAmount?: number;
    } = {}
  ): Promise<{
    data: Payment[];
    meta: any;
    summary: {
      totalAmount: number;
      totalTransactions: number;
      avgTransactionValue: number;
    };
  }> {
    try {
      const {
        page = 1,
        pageSize = 20,
        startDate,
        endDate,
        status,
        method,
        minAmount,
        maxAmount
      } = options;

      const filters: PaymentFilters = {};

      if (startDate) filters.dateFrom = startDate;
      if (endDate) filters.dateTo = endDate;
      if (status?.length) filters.status = status;
      if (method?.length) filters.method = method;
      if (minAmount) filters.amountMin = minAmount;
      if (maxAmount) filters.amountMax = maxAmount;

      const result = await this.getUserPayments(userId, filters, page, pageSize);

      const totalAmount = result.data.reduce((sum, p) => sum + (p.amount || 0), 0);
      const totalTransactions = result.data.length;
      const avgTransactionValue = totalTransactions > 0
        ? Math.round(totalAmount / totalTransactions)
        : 0;

      return {
        ...result,
        summary: {
          totalAmount,
          totalTransactions,
          avgTransactionValue
        }
      };
    } catch (error) {
      console.error('Get payment history error:', error);
      throw new Error('Không thể tải lịch sử thanh toán');
    }
  }

  /**
   * Export payment report
   */
  async exportPaymentReport(
    userId: string,
    format: 'csv' | 'excel' | 'pdf' = 'csv',
    filters?: PaymentFilters
  ): Promise<Blob> {
    try {
      const response = await apiClient.get<Blob>(
        '/api/payments/export',
        {
          params: {
            userId,
            format,
            ...filters
          },
          responseType: 'blob'
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Export payment report error:', error);
      throw new Error('Không thể xuất báo cáo thanh toán');
    }
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Validate payment data
   */
  private validatePaymentData(data: CreatePaymentData): void {
    if (!data.amount || data.amount <= 0) {
      throw new Error('Số tiền thanh toán không hợp lệ');
    }

    if (data.amount < 10000) {
      throw new Error('Số tiền thanh toán tối thiểu là 10,000 VND');
    }

    if (data.amount > 500000000) {
      throw new Error('Số tiền thanh toán tối đa là 500,000,000 VND');
    }

    if (!data.paymentMethod) {
      throw new Error('Phương thức thanh toán không được để trống');
    }

    if (data.customerEmail && !this.isValidEmail(data.customerEmail)) {
      throw new Error('Email không hợp lệ');
    }

    if (data.customerPhone && !this.isValidPhoneNumber(data.customerPhone)) {
      throw new Error('Số điện thoại không hợp lệ');
    }
  }

  /**
   * Format currency
   */
  formatCurrency(amount: number, currency: string = 'VND'): string {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency
    }).format(amount);
  }

  /**
   * Get payment status badge
   */
  getStatusBadge(status: PaymentStatus): {
    label: string;
    color: 'success' | 'warning' | 'error' | 'info' | 'default';
  } {
    const badges = {
      [PaymentStatus.PENDING]: { label: 'Chờ thanh toán', color: 'warning' as const },
      [PaymentStatus.PROCESSING]: { label: 'Đang xử lý', color: 'info' as const },
      [PaymentStatus.COMPLETED]: { label: 'Thành công', color: 'success' as const },
      [PaymentStatus.FAILED]: { label: 'Thất bại', color: 'error' as const },
      [PaymentStatus.CANCELLED]: { label: 'Đã hủy', color: 'default' as const },
      [PaymentStatus.REFUNDED]: { label: 'Đã hoàn tiền', color: 'info' as const },
      [PaymentStatus.PARTIALLY_REFUNDED]: { label: 'Hoàn một phần', color: 'info' as const }
    };

    return badges[status] || { label: 'Không xác định', color: 'default' };
  }

  /**
   * Get payment method icon
   */
  getMethodIcon(method: PaymentMethod): string {
    const icons: Record<PaymentMethod, string> = {
      [PaymentMethod.MOMO]: '💰',
      [PaymentMethod.VNPAY]: '🏦',
      [PaymentMethod.ZALOPAY]: '💙',
      [PaymentMethod.BANK_TRANSFER]: '🏛️',
      [PaymentMethod.CREDIT_CARD]: '💳',
      [PaymentMethod.DEBIT_CARD]: '💳',
      [PaymentMethod.EWALLET]: '💛',
      [PaymentMethod.CASH]: '💵',
      [PaymentMethod.CRYPTO]: '₿'
    };

    return icons[method] || '💳';
  }

  /**
   * Get payment method name
   */
  getMethodName(method: PaymentMethod): string {
    const names: Record<PaymentMethod, string> = {
      [PaymentMethod.MOMO]: 'MoMo',
      [PaymentMethod.VNPAY]: 'VNPay',
      [PaymentMethod.ZALOPAY]: 'ZaloPay',
      [PaymentMethod.BANK_TRANSFER]: 'Chuyển khoản ngân hàng',
      [PaymentMethod.CREDIT_CARD]: 'Thẻ tín dụng',
      [PaymentMethod.DEBIT_CARD]: 'Thẻ ghi nợ',
      [PaymentMethod.EWALLET]: 'Ví điện tử',
      [PaymentMethod.CASH]: 'Tiền mặt',
      [PaymentMethod.CRYPTO]: 'Tiền điện tử'
    };

    return names[method] || method;
  }

  // ========================================
  // PRIVATE HELPER METHODS
  // ========================================

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private isValidPhoneNumber(phone: string): boolean {
    const phoneRegex = /^(\+84|84|0)(3|5|7|8|9)[0-9]{8}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  }

  private getPeriodStartDate(period: string): string {
    const now = new Date();
    const startDate = new Date();

    switch (period) {
      case '7d':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(now.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(now.getDate() - 90);
        break;
      case '1y':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate.setDate(now.getDate() - 30);
    }

    return startDate.toISOString();
  }

  private getThisMonthPayments(payments: Payment[]): Payment[] {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return payments.filter(p => new Date(p.createdAt) >= startOfMonth);
  }

  private getLastMonthPayments(payments: Payment[]): Payment[] {
    const now = new Date();
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    return payments.filter(p => {
      const date = new Date(p.createdAt);
      return date >= startOfLastMonth && date <= endOfLastMonth;
    });
  }

  private calculateMethodStats(payments: Payment[]): Array<{
    method: string;
    count: number;
    amount: number;
  }> {
    const methodStats: Record<string, { count: number; amount: number }> = {};

    payments.forEach(payment => {
      const method = payment.paymentMethod || 'unknown';
      if (!methodStats[method]) {
        methodStats[method] = { count: 0, amount: 0 };
      }
      methodStats[method].count++;
      methodStats[method].amount += payment.amount || 0;
    });

    return Object.entries(methodStats)
      .map(([method, stats]) => ({ method, ...stats }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }
}

// Singleton export
export const paymentServiceV2 = new PaymentServiceV2();
export default paymentServiceV2;
